import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { GoogleMap, InfoWindowF, MarkerF, useJsApiLoader } from '@react-google-maps/api'
import { AlertTriangle, ArrowRight } from 'lucide-react'
import { usePageHeader } from '@/components/page-header-context'
import { useLiveMap } from '@/features/live-map/use-live-map'
import { toDriverMarkers } from '@/features/live-map/live-map.mappers'

const TUNIS_CENTER = { lat: 36.8065, lng: 10.1815 }
const MAP_ZOOM = 12
const mapContainerStyle = { width: '100%', height: '100%' }

function markerIcon(color: string): google.maps.Symbol | undefined {
  if (typeof window === 'undefined' || !window.google) return undefined
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 8,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#FFFFFF',
    strokeWeight: 2,
  }
}

function formatStart(startedAt: string | null): string {
  if (!startedAt) return '—'
  return new Date(startedAt).toLocaleTimeString('fr-TN', { hour: '2-digit', minute: '2-digit' })
}

export default function LiveMapPage() {
  const mapApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''
  const { data, isError } = useLiveMap()
  const drivers = data?.drivers ?? []
  const onRide = drivers.filter((d) => d.status === 'ON_RIDE').length

  usePageHeader(
    'Carte temps réel',
    `${drivers.length} chauffeur${drivers.length > 1 ? 's' : ''} en ligne · ${onRide} en course`,
  )

  // Mémoïsé sur `data` : les marqueurs ne sont recréés qu'au changement de
  // données (pas à chaque rendu), ce qui évite de faire clignoter la carte.
  const markers = useMemo(() => toDriverMarkers(data?.drivers ?? []), [data])

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = markers.find((m) => m.id === selectedId) ?? null

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: mapApiKey,
  })

  if (!mapApiKey) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center gap-2">
        <AlertTriangle className="text-danger" size={28} />
        <p className="text-sm font-semibold text-ink">Clé Google Maps non configurée</p>
        <p className="text-[13px] text-muted">
          Définissez <code className="font-mono">VITE_GOOGLE_MAPS_API_KEY</code> dans votre fichier{' '}
          <code className="font-mono">.env</code> (voir <code>.env.example</code>).
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      {isError && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-[12px] bg-danger/10 border border-danger/30">
          <AlertTriangle size={16} className="text-danger shrink-0" />
          <span className="text-[13px] font-medium text-danger">
            Données indisponibles — nouvelle tentative automatique en cours…
          </span>
        </div>
      )}

      <div className="flex-1 min-h-0 rounded-[18px] overflow-hidden border border-line bg-surface">
        {!isLoaded ? (
          <div className="flex items-center justify-center h-full text-muted">
            Chargement de la carte…
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={TUNIS_CENTER}
            zoom={MAP_ZOOM}
            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
          >
            {markers.map((m) => (
              <MarkerF
                key={m.id}
                position={m.position}
                icon={markerIcon(m.color)}
                onClick={() => m.status === 'ON_RIDE' && setSelectedId(m.id)}
              />
            ))}

            {selected?.activeRide && (
              <InfoWindowF position={selected.position} onCloseClick={() => setSelectedId(null)}>
                <div className="min-w-[200px] text-[#1A1A2E]">
                  <div className="text-[13px] font-bold">{selected.activeRide.passengerName}</div>
                  <div className="mt-1.5 flex items-center gap-1 text-[12px]">
                    <span className="truncate max-w-[80px]">
                      {selected.activeRide.pickupAddress}
                    </span>
                    <ArrowRight size={12} className="shrink-0" />
                    <span className="truncate max-w-[80px]">
                      {selected.activeRide.dropoffAddress}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-[#6b7280]">
                    Début : {formatStart(selected.activeRide.startedAt)}
                  </div>
                  <Link
                    to="/rides"
                    className="mt-2 inline-block text-[12px] font-semibold text-[#E94560]"
                  >
                    Voir le détail →
                  </Link>
                </div>
              </InfoWindowF>
            )}
          </GoogleMap>
        )}
      </div>
    </div>
  )
}
